// src/components/agent/ChatThread.js
//
// Replaces MessageDisplay (frontend spec §8.7): maps ViewMessages to the part
// renderers with the §3.4 layout — user bubbles right in primary.main,
// assistant content bubble-LESS on the page surface. Carries v1's near-bottom
// autoscroll heuristic plus a "jump to latest" button when detached.
import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Stack, Chip, Paper, Typography, Fab, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EditIcon from '@mui/icons-material/Edit';
import ChatTable from '../assistant/ChatTable';
import MarkdownBlock from './parts/MarkdownBlock';
import ToolCallChip from './parts/ToolCallChip';
import ChartsBlock from './parts/ChartsBlock';
import FileCard, { getAttachmentIcon } from './parts/FileCard';
import ImagesBlock from './parts/ImagesBlock';
import NoticeRow, { isDuplicateSendMessage } from './parts/NoticeRow';
import ApprovalCard from './parts/ApprovalCard';
import ThinkingIndicator from './parts/ThinkingIndicator';

const CursorBlink = styled('span')({
  display: 'inline-block',
  width: '8px',
  height: '1.2em',
  backgroundColor: 'currentColor',
  marginLeft: '2px',
  animation: 'blink 1s step-end infinite',
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
});

const UserBubble = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(1, 1.5),
  maxWidth: '70%',
  width: 'fit-content',
  alignSelf: 'flex-end',
}));

const getTextContent = (parts = []) =>
  parts
    .filter((p) => p.kind === 'text' || p.kind === 'user_text')
    .map((p) => p.text)
    .join('');

const UserMessageRow = ({ message, onOpenFile }) => {
  const noticeParts = message.parts.filter((p) => p.kind === 'notice');
  const bubbleParts = message.parts.filter((p) => p.kind !== 'notice');
  const text = getTextContent(bubbleParts);
  const chipParts = bubbleParts.filter(
    (p) => p.kind === 'upload' || (p.kind === 'images' && (!p.image_assets || p.image_assets.length === 0))
  );
  const imageParts = bubbleParts.filter((p) => p.kind === 'images');
  const canvasEditParts = bubbleParts.filter((p) => p.kind === 'canvas_edit');
  const bubbleEmpty = !text && chipParts.length === 0 && imageParts.length === 0 && canvasEditParts.length === 0;

  return (
    <>
      {/* Wake notifications must not look like the user spoke (§6.2). */}
      {noticeParts.map((part, i) => (
        <NoticeRow key={`notice-${i}`} kind={part.noticeKind} text={part.text} />
      ))}
      {!bubbleEmpty && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <UserBubble>
            {!!text && <MarkdownBlock text={text} userBubble />}
            {chipParts.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: text ? 1 : 0, flexWrap: 'wrap', gap: 1 }}>
                {chipParts.map((part, i) => (
                  <Chip
                    key={`attachment-${i}`}
                    icon={getAttachmentIcon(part.filename, part.fileKind)}
                    label={part.filename || 'Attachment'}
                    size="small"
                    variant="outlined"
                    // Persisted uploads open in the file pane (view + download);
                    // a still-pending send has no stored path yet.
                    onClick={part.gcsPath && onOpenFile
                      ? () => onOpenFile({ filename: part.filename, downloadUrl: null, gcsPath: part.gcsPath, sandboxPath: part.sandboxPath || null, toolUseId: null })
                      : undefined}
                    sx={{
                      color: 'inherit',
                      borderColor: 'rgba(255,255,255,0.4)',
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                ))}
              </Stack>
            )}
            {imageParts.map((part, i) => (
              <ImagesBlock key={`image-part-${i}`} part={part} onOpenFile={onOpenFile} />
            ))}
            {canvasEditParts.map((part, i) => (
              <Paper
                key={`canvas-edit-${i}`}
                variant="outlined"
                sx={{ p: 1, mt: 1, display: 'flex', alignItems: 'center', gap: 1, borderColor: 'rgba(255,255,255,0.3)', backgroundColor: 'transparent', color: 'inherit' }}
              >
                <EditIcon fontSize="small" sx={{ color: 'inherit', opacity: 0.7 }} />
                <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>
                  Edited canvas: {part.source_filename} ({part.edit_count || 0} edit{(part.edit_count || 0) !== 1 ? 's' : ''})
                </Typography>
              </Paper>
            ))}
          </UserBubble>
        </Box>
      )}
    </>
  );
};

const AssistantMessageRow = ({ message, onOpenFile }) => {
  const live = message.live;
  const text = getTextContent(message.parts);
  const lastPart = message.parts.length > 0 ? message.parts[message.parts.length - 1] : null;
  const showCursor = !!(live?.streaming && !live.thinking && !live.compacting && text && lastPart?.kind === 'text');
  // When an approval card is present it IS the control, so the paused notice
  // would only be redundant — the card already explains what is waiting.
  const hasApproval = message.parts.some((p) => p.kind === 'approval');
  // A turn that stopped without finishing (its lease expired, or the user
  // stopped it) leaves no result for a call it never completed; the chip shows
  // that instead of spinning forever (X13-9).
  const turnStopped = !!(message.interruptedTail || message.stoppedByUser);

  return (
    <Stack spacing={1} sx={{ width: '100%', alignItems: 'flex-start' }}>
      {message.parts.map((part, i) => {
        switch (part.kind) {
          case 'text':
            return (
              <Box key={i} sx={{ width: '100%', color: 'text.primary' }}>
                <MarkdownBlock text={part.text} />
                {showCursor && i === message.parts.length - 1 && <CursorBlink />}
              </Box>
            );
          case 'tool_call':
            // An idempotent duplicate-send outcome is a SUCCESSFUL tool result
            // (§7.16): the email was deliberately not submitted twice, so it is
            // never shown with the error treatment.
            return (
              <ToolCallChip
                key={i}
                part={isDuplicateSendMessage(part.statusMessage) ? { ...part, isError: false } : part}
                turnStopped={turnStopped}
              />
            );
          case 'charts':
            return (
              <Box key={i} sx={{ width: '100%' }}>
                <ChartsBlock chartGcsPath={part.gcsPath} />
              </Box>
            );
          case 'table':
            return (
              <Box key={i} sx={{ width: '100%' }}>
                <ChatTable tableGcsPath={part.gcsPath} />
              </Box>
            );
          case 'file':
            return (
              <FileCard
                key={i}
                filename={part.filename}
                description={part.description}
                downloadUrl={part.downloadUrl}
                gcsPath={part.gcsPath}
                sandboxPath={part.sandboxPath}
                toolUseId={part.toolUseId}
                onOpenFile={onOpenFile}
              />
            );
          case 'notice':
            return <NoticeRow key={i} kind={part.noticeKind} text={part.text} />;
          case 'approval':
            // Keyed by the durable approval id, so the one deduplicated card
            // keeps its identity while the record advances from pending to its
            // retained approved/denied outcome (§7.16).
            return (
              <Box key={part.approvalId} sx={{ alignSelf: 'stretch' }}>
                <ApprovalCard part={part} />
              </Box>
            );
          default:
            return null;
        }
      })}
      {live?.compacting && <ThinkingIndicator variant="compacting" />}
      {live?.streaming && !live.compacting && (live.thinking || !text) && (
        <ThinkingIndicator variant="thinking" thinkingText={live.thinkingText} />
      )}
      {/* Headless in_progress docs with a live turn behind them (§6.2). */}
      {!live && message.working && <ThinkingIndicator variant="working" />}
      {message.interruptedTail && (
        <Box sx={{ alignSelf: 'stretch' }}>
          <NoticeRow kind="interrupted" text="This response was interrupted" />
        </Box>
      )}
      {message.stoppedByUser && (
        <Box sx={{ alignSelf: 'stretch' }}>
          <NoticeRow kind="interrupted" text="Stopped at your request; later steps were not run" />
        </Box>
      )}
      {message.paused && !hasApproval && (
        <Box sx={{ alignSelf: 'stretch' }}>
          <NoticeRow kind="paused" text="Paused — waiting for approval" />
        </Box>
      )}
    </Stack>
  );
};

// A queued send (steer or release-window) staged at the end of the thread:
// the user's bubble, greyed, until the send actually fires and the real
// full-colour message takes its place.
const QueuedMessageRow = ({ preview }) => (
  <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
      <UserBubble>
        {!!preview.text && <MarkdownBlock text={preview.text} userBubble />}
        {preview.fileNames.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: preview.text ? 0.5 : 0 }}>
            {preview.fileNames.map((name, i) => (
              <Chip key={`${name}-${i}`} label={name} size="small" />
            ))}
          </Box>
        )}
      </UserBubble>
    </Box>
  </Box>
);

// The row this far from the top of what is loaded asks for the next page of
// history when it scrolls into view.
const LOAD_OLDER_ROW = 49;

function ChatThread({ messages, queuedPreview, onOpenFile, hasOlder = false, loadingOlder = false, onLoadOlder }) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const rowRefs = useRef(new Map());
  const prevLastIdRef = useRef(null);
  const loadAnchorRef = useRef(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  // A new user message (a send, from this tab or another) re-engages
  // autoscroll, mirroring v1's re-enable on send. Keyed on the last message's
  // identity, not the count: history paging in at the top is not a send.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && last.id !== prevLastIdRef.current && last.role === 'user') setAutoScrollEnabled(true);
    prevLastIdRef.current = last?.id ?? null;
  }, [messages]);

  // Ask for older history when the LOAD_OLDER_ROW-th loaded row is on screen.
  // The anchor records where that row sits so the page prepended above it can
  // be compensated for, leaving the reader exactly where they were.
  const sentinelId = hasOlder && !loadingOlder && onLoadOlder
    ? messages.slice(LOAD_OLDER_ROW).find((m) => m.role !== 'system')?.id ?? null
    : null;
  useEffect(() => {
    if (!sentinelId) return undefined;
    const row = rowRefs.current.get(sentinelId);
    const root = containerRef.current;
    if (!row || !root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      loadAnchorRef.current = { id: sentinelId, top: row.getBoundingClientRect().top, firstId: messages[0]?.id };
      onLoadOlder();
    }, { root });
    observer.observe(row);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelId, onLoadOlder]);

  useLayoutEffect(() => {
    const anchor = loadAnchorRef.current;
    if (!anchor || messages[0]?.id === anchor.firstId) return; // nothing prepended yet
    const row = rowRefs.current.get(anchor.id);
    const root = containerRef.current;
    if (row && root) root.scrollTop += row.getBoundingClientRect().top - anchor.top;
    loadAnchorRef.current = null;
  }, [messages]);

  const rowRef = (id) => (el) => {
    if (el) rowRefs.current.set(id, el); else rowRefs.current.delete(id);
  };

  // While auto-scroll is on, follow every change in the transcript's height —
  // streamed text, new messages, and tables, charts or files that hydrate
  // asynchronously after their message arrived. Observing the content box
  // rather than diffing the messages catches growth no message field records.
  // The container is observed too: the composer growing shrinks the viewport,
  // which hides the bottom just as surely as the transcript growing does.
  useEffect(() => {
    if (!autoScrollEnabled) return undefined;
    const scrollToEnd = () => messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    scrollToEnd();
    const observer = new ResizeObserver(scrollToEnd);
    if (contentRef.current) observer.observe(contentRef.current);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [autoScrollEnabled]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nextEnabled = distanceFromBottom < 100;
    setAutoScrollEnabled((prev) => (nextEnabled !== prev ? nextEnabled : prev));
  };

  // Smooth-scroll down and let handleScroll re-engage auto-scroll on arrival;
  // enabling it up front would start the follow effect, whose instant scroll
  // cuts the animation short. Re-enable directly only when already within
  // range, where no scroll event would come.
  const jumpToLatest = () => {
    const el = containerRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      setAutoScrollEnabled(true);
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        // History pages in above the viewport; the layout effect above holds the
        // reader's place, so the browser's own scroll anchoring must not also.
        sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, overflowAnchor: 'none' }}
      >
        <Box ref={contentRef} sx={{ maxWidth: 800, mx: 'auto', width: '100%', display: 'flex', flexDirection: 'column' }}>
          {messages.map((message) => {
            if (message.role === 'system') {
              if (message.compaction) {
                return <NoticeRow key={message.id} kind="compaction" text="Conversation summarised" />;
              }
              if (message.interrupted) {
                return <NoticeRow key={message.id} kind="interrupted" text="This response was interrupted" />;
              }
              return null;
            }
            return (
              <Box key={message.id} ref={rowRef(message.id)} sx={{ py: 1, display: 'flex', flexDirection: 'column' }}>
                {message.role === 'user' ? (
                  <UserMessageRow message={message} onOpenFile={onOpenFile} />
                ) : (
                  <AssistantMessageRow message={message} onOpenFile={onOpenFile} />
                )}
              </Box>
            );
          })}
          {queuedPreview && <QueuedMessageRow preview={queuedPreview} />}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
      {loadingOlder && (
        <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 999, bgcolor: 'background.paper', border: 1, borderColor: 'divider', boxShadow: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading earlier messages</Typography>
        </Box>
      )}
      {!autoScrollEnabled && (
        <Fab
          size="small"
          onClick={jumpToLatest}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'background.alt',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            '&:hover': { backgroundColor: 'background.paper' },
          }}
          aria-label="Jump to latest"
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}
    </Box>
  );
}

export default ChatThread;
